<template>
  <div class="discussion-container basic-flex">
    <DynamicScroller :page-mode="pageMode" :items="messageGroupsList" :min-item-size="size" class="discussion scroll basic-flex" key-field="uuid" ref="scrollList" :buffer="1000">
      <template v-slot="{ item, index, active }">
        <DynamicScrollerItem :item="item" :active="active" :size-dependencies="getMessages(item)" :index="index" :data-index="index">
          <!-- allowPin is about privileges, showPin is about logic of when and where it should be shown -->
          <ChatMessage v-bind="item"
              :raw-messages="rawMessages"
              :pinned-messages="pinnedMessages"
              :allow-pin="!!userProfile.privileges?.[privileges.TEXT_CHAT_PIN_MESSAGES]"
              :allow-edit="!!userProfile.privileges?.[privileges.TEXT_CHAT_EDIT_MESSAGES]"
              :allow-delete="!!userProfile.privileges?.[privileges.TEXT_CHAT_DELETE_MESSAGES]"
              :show-pin="(roomOwner || roomController)"
              :show-edit="item.sender === 'self'"
              :show-delete="(item.sender === 'self' || roomOwner)"
              :ref="item.uuid"
              :user-stylesheet="userStylesheet"
              :room-users="roomUsers"
              :editing="editingMessageID"
              @update:editing="msgID => { editingMessageID = msgID }"
              @pin="onPin"
              @edit="onEdit"
              @delete="onDelete"/>
        </DynamicScrollerItem>
      </template>
    </DynamicScroller>
    <div class="pinned-messages scroll" v-show="Object.keys(pinnedMessages).length > 0">
      <ChatMessage v-for="message in pinnedMessages"
          :key="message.uuid"
          v-bind="message"
          :allow-unpin="!!userProfile.privileges?.[privileges.TEXT_CHAT_PIN_MESSAGES]"
          :show-unpin="(roomOwner || roomController)"
          :messages="[{uuid: message.uuid}]"
          :raw-messages="pinnedMessages"
          :pinned-messages="pinnedMessages"
          :room-users="roomUsers"
          class="pinned-message"
          @pin="onPin"> <!-- reusing pin event for unpin as well -->
          <template slot="from-slot">
            <i-mdi-pin width="19" height="19" class="pin"></i-mdi-pin>
          </template>
      </ChatMessage>
    </div>
    <a v-show="shouldShowScrollButton" class="btn-scroll-bottom" @click="forceScrollToBottom">
      <i-mdi-chevron-double-down width="16" height="16" title="Scroll to bottom"/>
    </a>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import throttle from 'lodash/throttle.js'
import AppBus from '@C/bus/app-bus.vue'
import { scrollTo } from '@/js/scroll.js'
import { waitForDOMNode } from '@/js/utils.js'
import { RoomEventTypes } from '@root/backend/databases/events.js'
import { privileges } from '@root/backend/patron-privileges/privileges.js'
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller'
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'

export default {
  name: 'discussion',
  components: {
    DynamicScroller,
    DynamicScrollerItem,
    ChatMessage: () => import(/* webpackChunkName: "chat-message" */ '@RC/communications/chat-message.vue')
  },
  props: {
    pageMode: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      shouldAutoScroll: true,
      disableOnScroll: false,
      shouldShowScrollButton: false,
      scrollDiff: 0, // How far are we from the bottom
      gotNewMessage: false,
      defaultWindowTitle: undefined,
      size: 67.5,
      editingMessage: undefined,
      editingMessageID: undefined
    }
  },
  computed: {
    ...mapGetters([
      'socket',
      'onSocketAvailable',
      'userProfile',
      'rawMessages',
      'messageGroupsList',
      'unreadMessages',
      'showingDiscussion',
      'roomUsers',
      'roomOwner',
      'roomController',
      'userStylesheet',
      'pinnedMessages'
    ]),
    privileges () {
      return privileges
    }
  },
  watch: {
    scrollDiff (v) {
      // console.log(`scrollDiff=${v}`)
      if (v < -40) {
        this.shouldShowScrollButton = true
      } else {
        this.shouldShowScrollButton = false
      }
      this.shouldAutoScroll = v >= -40
    },
    messageGroupsList: {
      deep: true,
      async handler () {
        await this.$nextTick()
        if (this.shouldAutoScroll) {
          await this.scrollToBottom(true, 10)
        }
      }
    }
  },
  methods: {
    getMessages (item) {
      return item.messages.map(({ uuid }) => this.rawMessages[uuid])
    },
    onScroll: throttle(function () {
      if (this.disableOnScroll) {
        return
      }
      this.updateScrollDiff()
    }, 30),
    updateScrollDiff () {
      const { $discussionEl } = this
      const { scrollTop, scrollHeight, offsetHeight } = $discussionEl
      this.scrollDiff = scrollTop - (scrollHeight - offsetHeight)
    },
    onUpdate (startIndex, endIndex) {
    },
    getScrollBottom () {
      const { $discussionEl } = this
      let accumulator = 0
      try {
        accumulator = this.$refs.scrollList.$children[0].sizes[this.messageGroupsList.length - 1].accumulator
      } catch (e) {
      }
      const height = $discussionEl.children[0].scrollHeight
      const bottom = Math.max(height, accumulator)
      // console.log(`height=${height} bottom=${bottom}`)
      return bottom
    },
    async scrollToBottom (smooth = true, duration = 300) {
      this.disableOnScroll = true
      const { $discussionEl } = this
      const diff = Math.abs($discussionEl.scrollTop - $discussionEl.children[0].scrollHeight)
      if (smooth && diff > 2000) {
        smooth = false
      }
      // console.log(`Scrolling to bottom=${bottom} smooth=${smooth}`)
      if (smooth) {
        const bottom = this.getScrollBottom()
        await new Promise(resolve => scrollTo(this.$discussionEl, bottom, smooth ? duration : 0, undefined, resolve))
      } else {
        this.$refs.scrollList.scrollToBottom()
      }
      this.disableOnScroll = false
    },
    forceScrollToBottom () {
      this.scrollDiff = 0
      this.scrollToBottom(true)
    },
    async onPin (data) {
      this.socket.emit(RoomEventTypes.ROOM_PINNED_MESSAGE_UPDATE, data)
    },
    async onEdit (data) {
      this.socket.emit(RoomEventTypes.ROOM_EDIT_CHAT_MESSAGE, data)
    },
    async onDelete (data) {
      this.socket.emit(RoomEventTypes.ROOM_DELETE_CHAT_MESSAGE, data)
    },
    handleEdit () {
      // Check if this element is visible. If not, bail
      const { $el, messageGroupsList } = this
      if ($el.offsetParent === null) {
        // We're hidden. Bail.
        return
      }
      // If user does not have access to edit, bail as well
      if (!this.userProfile.privileges?.[this.privileges.TEXT_CHAT_EDIT_MESSAGES]) {
        AppBus.$emit('edit-complete')
        return
      }
      for (let idx = messageGroupsList.length - 1; idx >= 0; idx--) {
        const message = messageGroupsList[idx]
        const { sender } = message
        if (sender !== 'self') {
          continue
        }
        // We have a message
        const { messages: messageIDs } = message
        this.editingMessage = message
        message.editing = messageIDs[messageIDs.length - 1].uuid
        const unwatch = this.$watch('editingMessageID', () => {
          unwatch()
          AppBus.$emit('edit-complete')
        })
        break
      }
    }
  },
  beforeDestroy () {
    AppBus.$off('edit-last-message', this.handleEdit)
  },
  async mounted () {
    this.$discussionEl = await waitForDOMNode({ type: 'selector', value: '.discussion' }, this.$el)
    this.$discussionEl.addEventListener('scroll', this.onScroll)
    AppBus.$on('edit-last-message', this.handleEdit)
  }
}
</script>

<style lang="scss" scoped>
.dark, .theater-mode {
  .discussion-div {
    background: $chat-dark-bg;
    color: $dark-text;
    .discussion-container {
      .btn-scroll-bottom {
        &:focus {
          border: none;
          background-color: $dark-grey-bg;
          color: lighten($dark-text, 10);
        }
      }
    }
    .discussion {
      .edit-msg {
        background-color: lighten($chat-dark-bg, 20) !important;
      }
    }
  }
  .discussion-container {
    ::v-deep .pinned-messages {
      .pin svg {
        fill: $dark-text !important;
      }
    }
  }
}

.discussion-container {
  position: relative;
  display: flex;
  overflow-y: hidden;
  overflow-x: hidden;
  height: 100%;
  .pinned-messages {
    margin-top: 4px;
    max-height: 30%;
    overflow-y: auto;
    ::v-deep .first-message {
      overflow: hidden;
    }
    ::v-deep .chat-message-div {
      flex: 1;
      .from-container {
        display: flex;
        flex: 1;
        p {
          flex: 1;
        }
        .pin svg {
          transform: rotate(45deg);
          fill: var(--primary-color);
        }
      }
    }
  }
  .btn-scroll-bottom {
    position: absolute;
    display: flex;
    flex-direction: column;
    flex: 1;
    align-items: center;
    justify-content: center;
    margin-right: 0;
    bottom: 10px;
    right: 0;
    width: 24px;
    height: 24px;
    border-radius: 50% !important;
    opacity: 0.8;
    flex-shrink: 0;
    background: #ffffff;
    color: #000;
    > span {
      font-size: 12px;
      line-height: 12px;
    }
    &:focus {
      background-color: $primary-color;
    }
  }
}

.discussion {
  position: relative;
  list-style: none;
  margin: 0;
  margin-bottom: 4px;
  padding: 0 0 0px 0; // finality
  flex: 1;
  height: 100% !important;
  @include until($desktop) {
  }
  &.smooth-scroll {
    scroll-behavior: smooth;
  }

  .edit-msg {
    background-color: darken($light-background, 5);
    border-radius: 5px;
    margin: 2px 2px 2px 0;
    min-height: 20px;
    max-height: 128px;
    font-size: 1rem;
    line-height: 1rem;
    padding: 8px;
    height: initial;
    border: none;
    outline: none;
  }
}

</style>
